import { useEffect, useMemo, useState } from "react";
import s from "./EnrollmentsPanel.module.css";

/** Filter-first view of the enrollments table. Mirrors the ClassOfferingsPanel
 *  layout (SY + Semester + Search + New) so admins keep a consistent workflow:
 *  pick the term you care about, then scan or search. The table markup itself
 *  is delegated to the parent's renderCell + column list so all tabs share
 *  one look. */
export default function EnrollmentsPanel({
  enrollments,
  schoolYears,
  semesters,
  students,
  sections,
  gradeLevels,
  columns,
  renderCell,
  onNew,
  onEdit,
  onDelete,
  pageStyles,  // parent page's css module — we reuse .table, .tableWrap, etc.
  // Optional one-shot deep-link filter from cross-page navigation. When
  // present on mount (or first non-null), we pre-fill SY/semester and
  // jump to the section view so the linked section is visible. Section
  // is also kept around so the panel can surface a transient highlight.
  initialFilter,
}) {
  const [syId,      setSyId]      = useState("");
  const [semId,     setSemId]     = useState("");
  const [search,    setSearch]    = useState("");
  const [viewMode,  setViewMode]  = useState("grade"); // "grade" | "section" | "table" | "pending"
  const [showEmpty, setShowEmpty] = useState(true);
  // Section highlight (from deep-link). The matching section card pulses
  // briefly so admins land oriented; cleared on next render after ~3s.
  const [highlightSectionId, setHighlightSectionId] = useState(null);

  // Apply the deep-link filter exactly once. Guarded so a parent that
  // re-renders with the same prop doesn't keep re-applying it (and
  // wiping the admin's subsequent edits to SY/Semester).
  const [filterApplied, setFilterApplied] = useState(false);
  useEffect(() => {
    if (filterApplied || !initialFilter) return;
    if (initialFilter.schoolYearId != null) setSyId(String(initialFilter.schoolYearId));
    if (initialFilter.semesterId   != null) setSemId(String(initialFilter.semesterId));
    if (initialFilter.sectionId    != null) {
      setViewMode("section");
      setHighlightSectionId(initialFilter.sectionId);
      const t = setTimeout(() => setHighlightSectionId(null), 3000);
      setFilterApplied(true);
      return () => clearTimeout(t);
    }
    setFilterApplied(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  // Default SY + Semester to the active pair on first load.
  useEffect(() => {
    // Don't clobber a pending deep-link's SY before it gets applied.
    if (initialFilter && !filterApplied) return;
    const activeSy = (schoolYears || []).find(y => y.active);
    if (activeSy && !syId) setSyId(String(activeSy.id));
  }, [schoolYears, syId, initialFilter, filterApplied]);

  useEffect(() => {
    if (!syId) return;
    // Keep Semester consistent with SY: if the current semester doesn't belong
    // to the selected SY, swap it for that SY's active (or first) semester.
    const inSy = (semesters || []).filter(x => String(x.schoolYearId) === String(syId));
    const stillValid = inSy.some(x => String(x.id) === semId);
    if (stillValid) return;
    const fresh = inSy.find(x => x.active) || inSy[0];
    setSemId(fresh ? String(fresh.id) : "");
  }, [syId, semesters, semId]);

  const studentById = useMemo(
    () => Object.fromEntries((students || []).map(r => [r.id, r])),
    [students]
  );
  const sectionById = useMemo(
    () => Object.fromEntries((sections || []).map(r => [r.id, r])),
    [sections]
  );
  const gradeById = useMemo(
    () => Object.fromEntries((gradeLevels || []).map(g => [g.id, g])),
    [gradeLevels]
  );

  /** Students who are NOT yet represented in `enrollments` — either their
   *  status says PENDING or they simply have no currentSectionId. These
   *  never show up in the Grade/Section/Table views because those are
   *  driven by the enrollments list, so the Pending view is the only
   *  place they surface for the registrar to act on. */
  const pendingStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    const studentHasEnrollment = new Set((enrollments || []).map(e => e.studentId));
    return (students || [])
      .filter(st => {
        const isPending = (st.status || "PENDING").toUpperCase() === "PENDING";
        const noSection = !st.currentSectionId;
        const noEnrollment = !studentHasEnrollment.has(st.id);
        return isPending || (noSection && noEnrollment);
      })
      .filter(st => {
        if (!q) return true;
        const hay = [
          st.studentNumber,
          `${st.lastName || ""}, ${st.firstName || ""}`,
          st.status,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
  }, [students, enrollments, search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (enrollments || [])
      .filter(e => !syId  || String(e.schoolYearId) === String(syId))
      .filter(e => !semId || String(e.semesterId)   === String(semId))
      .filter(e => {
        if (!q) return true;
        const st  = studentById[e.studentId];
        const sec = sectionById[e.sectionId];
        const grade = sec ? gradeById[sec.gradeLevelId] : null;
        const hay = [
          st?.studentNumber,
          st ? `${st.lastName}, ${st.firstName}` : "",
          sec?.name,
          grade?.name,
          e.status,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      });
  }, [enrollments, syId, semId, search, studentById, sectionById, gradeById]);

  /** Group the filtered enrollments for the card views. Each group:
   *  {key, label, ordering, items[]}. `label` shows above each group as a
   *  header (e.g. "GRADE 5" or "GRADE 5 · St. Ignatius of Loyola").
   *
   *  When `showEmpty` is on we seed the map with every grade level (or every
   *  section in the selected SY) BEFORE bucketing enrollments, so grades or
   *  sections with zero enrollments still appear as empty group headers —
   *  helpful for spotting unfilled classrooms. */
  const grouped = useMemo(() => {
    if (viewMode === "table") return [];
    const map = new Map();

    if (showEmpty) {
      if (viewMode === "grade") {
        for (const g of (gradeLevels || [])) {
          map.set(`gl-${g.id}`, {
            key: `gl-${g.id}`,
            label: g.name,
            ordering: g.ordering ?? 9999,
            items: [],
          });
        }
      } else {
        // Section view: scope sections by SY filter — sections belong to a SY.
        const list = (sections || [])
          .filter(sec => !syId || String(sec.schoolYearId) === String(syId));
        for (const sec of list) {
          const grade = sec.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
          map.set(`sec-${sec.id}`, {
            key: `sec-${sec.id}`,
            label: grade ? `${grade.name} · ${sec.name}` : sec.name,
            ordering: (grade?.ordering ?? 9999) * 1000 + (sec.id ?? 0),
            items: [],
          });
        }
      }
    }

    for (const e of filtered) {
      const sec = e.sectionId ? sectionById[e.sectionId] : null;
      const grade = sec ? gradeById[sec.gradeLevelId] : null;

      let key, label, ordering;
      if (viewMode === "grade") {
        key      = grade ? `gl-${grade.id}` : "gl-none";
        label    = grade ? grade.name : "No grade level";
        ordering = grade?.ordering ?? 9999;
      } else {
        key      = sec ? `sec-${sec.id}` : "sec-none";
        label    = sec
          ? (grade ? `${grade.name} · ${sec.name}` : sec.name)
          : "No section assigned";
        ordering = (grade?.ordering ?? 9999) * 1000 + (sec?.id ?? 0);
      }
      if (!map.has(key)) map.set(key, { key, label, ordering, items: [] });
      map.get(key).items.push(e);
    }

    let out = [...map.values()];
    if (!showEmpty) out = out.filter(g => g.items.length > 0);
    return out.sort((a, b) => a.ordering - b.ordering);
  }, [viewMode, filtered, sections, gradeLevels, sectionById, gradeById, syId, showEmpty]);

  function studentInitials(st) {
    const a = st?.firstName?.[0] || "";
    const b = st?.lastName?.[0]  || "";
    return (a + b).toUpperCase() || "?";
  }

  function fmtDate(v) {
    if (!v) return "";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  }

  // Reuse the parent page's table styles so this panel looks like the rest of
  // the tabs. `pageStyles` is the DirectoryPage module passed down.
  const p = pageStyles || {};

  return (
    <div className={s.wrap}>
      {/* ── Filter bar ─────────────────────────────────────────────── */}
      <div className={s.filterRow}>
        <label className={s.filterField}>
          <span>School Year</span>
          <select value={syId} onChange={e => setSyId(e.target.value)}>
            <option value="">All years</option>
            {(schoolYears || []).map(y => (
              <option key={y.id} value={y.id}>
                {y.label}{y.active ? " · Active" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className={s.filterField}>
          <span>Semester</span>
          <select value={semId} onChange={e => setSemId(e.target.value)} disabled={!syId}>
            <option value="">All semesters</option>
            {(semesters || [])
              .filter(x => !syId || String(x.schoolYearId) === String(syId))
              .map(x => (
                <option key={x.id} value={x.id}>
                  {x.label}{x.active ? " · Active" : ""}
                </option>
              ))}
          </select>
        </label>
        <label className={`${s.filterField} ${s.searchField}`}>
          <span>Search</span>
          <div className={s.searchWrap}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
                 className={s.searchIcon} aria-hidden>
              <circle cx="9" cy="9" r="6" />
              <path d="M15 15l-3.5-3.5" />
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Student #, name, section, status…"
            />
            {search && (
              <button type="button" className={s.clearBtn} onClick={() => setSearch("")} aria-label="Clear">✕</button>
            )}
          </div>
        </label>
        <button className={s.newBtn} onClick={() => onNew()}>+ New</button>
      </div>

      {/* ── View toggle ───────────────────────────────────────────── */}
      <div className={s.viewRow}>
        <div className={s.segmentedWrap}>
          <span className={s.segLabel}>View</span>
          <div className={s.segmented} role="tablist" aria-label="View">
            {[
              ["grade",   "Grade Level"],
              ["section", "Section"],
              ["table",   "All Students (List form)"],
              ["pending", "Pending"],
            ].map(([val, lbl]) => {
              const isPending = val === "pending";
              return (
                <button
                  key={val}
                  type="button"
                  role="tab"
                  aria-selected={viewMode === val}
                  className={`${s.segBtn} ${viewMode === val ? s.segBtnActive : ""}`}
                  onClick={() => setViewMode(val)}>
                  {lbl}
                  {isPending && pendingStudents.length > 0 && (
                    <span className={s.segBadge}>{pendingStudents.length}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {(viewMode === "grade" || viewMode === "section") && (
          <label className={s.toggle}>
            <input
              type="checkbox"
              checked={showEmpty}
              onChange={e => setShowEmpty(e.target.checked)}
            />
            <span>
              Show empty {viewMode === "grade" ? "grade levels" : "sections"} (no enrollments yet)
            </span>
          </label>
        )}
        <span className={s.pill}>
          {viewMode === "pending"
            ? `${pendingStudents.length} pending`
            : `${filtered.length} enrollment${filtered.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {viewMode === "pending" ? (
        /* ── Pending-students view ─────────────────────────────────
           Students with status=PENDING or no currentSectionId — they
           don't surface anywhere else in this panel because the other
           views are driven by the enrollments table. One-click Enroll
           on each card opens the + New modal pre-filled with the
           student so the admin only picks a section. */
        pendingStudents.length === 0 ? (
          <div className={s.empty}>
            No pending students. Everyone has a section.
          </div>
        ) : (
          <div className={s.groupScroll}>
            <div className={s.group}>
              <div className={s.groupHead}>
                Pending enrollment
                <span className={s.groupCount}>
                  {pendingStudents.length} student{pendingStudents.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className={s.cardGrid}>
                {pendingStudents.map(st => (
                  <div key={st.id} className={s.enrollCard}>
                    <div className={s.enrollCardTop}>
                      <div className={s.avatar}>{studentInitials(st)}</div>
                      <div className={s.enrollCardBody}>
                        <div className={s.studentName}>
                          {st.lastName || ""}{st.firstName ? `, ${st.firstName}` : ""}
                        </div>
                        <div className={s.studentMeta}>
                          {st.studentNumber || ""}
                        </div>
                      </div>
                      <span className={`${s.statusBadge} ${s.status_PENDING || ""}`}>
                        {st.status || "PENDING"}
                      </span>
                    </div>
                    <div className={s.enrollCardMeta}>
                      <span>No section assigned yet</span>
                    </div>
                    <div className={s.enrollCardActions}>
                      <button type="button" className={p.btnPrimary}
                              onClick={() => onNew({ studentId: st.id })}>
                        Enroll
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      ) : viewMode === "table" ? (
        /* ── List form (existing table) ───────────────────────────── */
        filtered.length === 0 ? (
          <div className={s.empty}>
            {search || syId || semId
              ? "No enrollments match the current filters."
              : "No enrollments yet. Click + New to add one."}
          </div>
        ) : (
          <div className={p.tableWrap}>
            <table className={p.table}>
              <thead>
                <tr>
                  {columns.map(([k, label]) => <th key={k}>{label}</th>)}
                  <th className={p.actionsCol}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => (
                  <tr key={row.id}>
                    {columns.map(([k]) => <td key={k}>{renderCell(row, k)}</td>)}
                    <td className={p.actionsCol}>
                      <button className={p.btnGhost}  onClick={() => onEdit(row)}>Edit</button>
                      <button className={p.btnDanger} onClick={() => onDelete(row)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : grouped.length === 0 ? (
        <div className={s.empty}>
          {showEmpty
            ? (viewMode === "grade"
                ? "No grade levels configured yet."
                : "No sections for this school year yet.")
            : "No enrollments match the current filters."}
        </div>
      ) : (
        /* ── Grouped card views (Grade Level / Section) ──────────── */
        <div className={s.groupScroll}>
          {grouped.map(g => {
            const isHighlight = highlightSectionId != null && g.key === `sec-${highlightSectionId}`;
            return (
            <div
              key={g.key}
              className={`${s.group} ${isHighlight ? s.groupHighlight : ""}`}
              ref={isHighlight ? (el) => {
                // Auto-scroll the deep-linked section into view, once,
                // when the element first mounts. Smooth + not-too-aggressive.
                if (el && !el.dataset.scrolled) {
                  el.dataset.scrolled = "1";
                  setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
                }
              } : undefined}>
              <div className={s.groupHead}>
                {g.label}
                <span className={s.groupCount}>
                  {g.items.length} enrollment{g.items.length === 1 ? "" : "s"}
                </span>
              </div>
              {g.items.length === 0 ? (
                <div className={s.emptyGroup}>No enrollments yet</div>
              ) : (
              <div className={s.cardGrid}>
                {g.items.map(e => {
                  const st = studentById[e.studentId];
                  const sec = e.sectionId ? sectionById[e.sectionId] : null;
                  const grade = sec ? gradeById[sec.gradeLevelId] : null;
                  // In Grade view, the grade is in the header — show the
                  // section under the name instead. In Section view, the
                  // section is in the header, so show only the grade.
                  const subline = viewMode === "grade"
                    ? (sec ? (grade ? `${grade.name} · ${sec.name}` : sec.name) : "—")
                    : (grade?.name || "—");
                  return (
                    <div key={e.id} className={s.enrollCard}>
                      <div className={s.enrollCardTop}>
                        <div className={s.avatar}>{studentInitials(st)}</div>
                        <div className={s.enrollCardBody}>
                          <div className={s.studentName}>
                            {st ? `${st.lastName}, ${st.firstName}` : `Student #${e.studentId}`}
                          </div>
                          <div className={s.studentMeta}>
                            {st?.studentNumber || ""}
                          </div>
                        </div>
                        <span className={`${s.statusBadge} ${s[`status_${e.status}`] || ""}`}>
                          {e.status || "ENROLLED"}
                        </span>
                      </div>
                      <div className={s.enrollCardMeta}>
                        <span>{subline}</span>
                        {e.enrolledAt && <span>· {fmtDate(e.enrolledAt)}</span>}
                      </div>
                      <div className={s.enrollCardActions}>
                        <button type="button" className={p.btnGhost}  onClick={() => onEdit(e)}>Edit</button>
                        <button type="button" className={p.btnDanger} onClick={() => onDelete(e)}>Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
